<template>
  <!-- Container Query wrapper -->
  <div
    ref="containerEl"
    class="responsive-wrapper"
    :style="{ '--breakpoint': breakpointWidth + 'px' }"
  >
    <div :class="['flex', shouldBeRow ? 'flex-row' : 'flex-col', gap]">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { RESPONSIVE_WRAPPER_CONFIG } from '@/configs/layout.config';

interface Props {
  /**
   * Number of form field items in this wrapper.
   * Used to calculate when to switch from row to column layout.
   *
   * Breakpoint = itemCount × pixelsPerItem
   *
   * @default 2
   * @example
   * <ResponsiveWrapper :itemCount="3" />  // 3 × 200px = 600px breakpoint
   */
  itemCount?: number;

  /**
   * When true: flex-row from calculated breakpoint onwards, otherwise flex-col.
   * When false: always flex-row (no responsive behavior).
   * @default true
   */
  isAutoResponsive?: boolean;

  /**
   * Gap between form fields.
   * @default 'gap-0' (no space)
   */
  gap?: 'gap-0' | 'gap-2' | 'gap-3' | 'gap-4' | 'gap-6';
}

const props = withDefaults(defineProps<Props>(), {
  itemCount: 2,
  isAutoResponsive: true,
  gap: 'gap-0',
});

const containerEl = ref<HTMLElement>();
const containerWidth = ref(0);

/**
 * Dynamically calculated breakpoint width based on number of items
 * breakpoint = itemCount × pixelsPerItem
 */
const breakpointWidth = computed(() => {
  return props.itemCount * RESPONSIVE_WRAPPER_CONFIG.pixelsPerItem;
});

/**
 * Should elements be displayed in row (true) or column (false)?
 * When container is wider than breakpoint AND isAutoResponsive is true → show as row
 */
const shouldBeRow = computed(() => {
  if (!props.isAutoResponsive) {
    return true; // Always row if auto-responsive is disabled
  }
  return containerWidth.value >= breakpointWidth.value;
});

onMounted(() => {
  if (!containerEl.value) return;

  // Initial measurement
  containerWidth.value = containerEl.value.offsetWidth;

  // ResizeObserver to watch container width changes
  const observer = new ResizeObserver(() => {
    if (containerEl.value) {
      containerWidth.value = containerEl.value.offsetWidth;
    }
  });

  observer.observe(containerEl.value);

  onBeforeUnmount(() => {
    observer.disconnect();
  });
});
</script>

<style scoped>
.responsive-wrapper {
  /* CSS variable for debugging/styling if needed */
  --breakpoint: 400px;
}
</style>
